<template>
  <div id="outreach_templates" class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between align-center">
      <h1 class="text-xl text_color font-semibold">Value Prop Templates</h1>
      <a-button class="primary_medium" @click="showTemplateModal('add')"
        >Create Template</a-button
      >
    </div>

    <EmptyScreen
      v-if="templates.length <= 0"
      source="email_template"
      text="No templates created yet!"
    />

    <CustomTable
      v-else
      :headers="templateHeaders"
      :data="templates"
      :pagination="pagination"
      :show-actions-column="true"
      :custom-row="customRow"
      :actions="actions"
      @update:pagination="handlePageChange"
    />

    <add-template
      :drawerVisible="templateModalVisible"
      :is-editing="isTemplateEditing"
      :template-id="templateToEdit"
      @update:modelValue="templateModalVisible = false"
      @template-saved="onEditTemplate"
    />

    <CustomModal
      v-model:visible="deleteTemplateModalVisible"
      title="Delete Template"
      width="32vw">
      <span>Are you sure you want to delete this template?</span>
      <template #footer>
        <a-button class="primary_medium" @click="deleteTemplate"
          >Confirm
        </a-button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import AddTemplate from "@/src/views/campaign/AddTemplate.vue";
import OutreachService from "@/src/api/OutreachService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import {imageNames} from "@/src/constants/imageConstants.js";

const templates = ref([]);
const templateModalVisible = ref(false);
const deleteTemplateModalVisible = ref(false);
const isTemplateEditing = ref(false);
const templateToDelete = ref(null);
const templateToEdit = ref(null);

const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
});

const templateHeaders = [
  { label: "Template Name", key: "template_name", width: 12 },
];

const actions = [
  {
    label: "Edit",
    handler: (row) => showTemplateModal("edit", row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: (row) => showDeleteConfirm(row),
    icon: imageNames.DELETE_GREY,
  },
];

const customRow = (record) => {
  return {
    onClick: () => {
      showTemplateModal("edit", record);
    },
  };
};

const fetchTemplates = async () => {
  try {
    const response = await OutreachService.fetchCampaignTemplates();
    if (response.success) {
      templates.value = response.templates;
    } else {
      message.error("Failed to fetch templates");
    }
  } catch (error) {
    console.error("Error fetching templates:", error);
    message.error("An error occurred while fetching templates");
  }
};

const showDeleteConfirm = (record) => {
  deleteTemplateModalVisible.value = true;
  templateToDelete.value = record;
};

const handlePageChange = (newPagination) => {
  pagination.value = newPagination;
  fetchTemplates();
};

const showTemplateModal = (mode, template = null) => {
  if (mode === "edit" && template) {
    templateToEdit.value = template.id;
    isTemplateEditing.value = true;
  } else {
    templateToEdit.value = null;
    isTemplateEditing.value = false;
  }
  templateModalVisible.value = true;
};

const onEditTemplate = async () => {
  templateModalVisible.value = false;
  await fetchTemplates();
};

const deleteTemplate = async () => {
  try {
    const response = await OutreachService.deleteCampaignTemplate(
      templateToDelete.value.id,
    );
    if (response.success) {
      deleteTemplateModalVisible.value = false;
      message.success(response.message);
      await fetchTemplates();
    } else {
      message.error(response.message);
    }
  } catch (error) {
    console.error("Error deleting Template:", error);
    message.error("An error occurred while deleting the Template");
  }
};

onMounted(() => {
  fetchTemplates();
});
</script>
